/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  FieldValues, SubmitHandler,
} from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  AlertTitle,
  TextField,
  Button,
  Stack,
  Box,
  MenuItem,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import {
  useForm,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { z } from 'zod';

const TripReferralSchema = z.object({
  referTo: z.string().min(1, 'Select a referral type'),
  referralComments: z.string().min(1, { message: 'Please add a comment' }),
});

type TripEditProps = {
  onCancel: () => void;
  onSubmit: SubmitHandler<FieldValues>;
  submissionError?: ApiError;
};

const TripRequestReferralForm = ({
  onCancel,
  onSubmit,
  submissionError = undefined,
}: TripEditProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    register,
  } = useForm({
    defaultValues: {
      referTo: 'ETAV',
      referralComments: '',
    },
    resolver: zodResolver(TripReferralSchema),
  });

  const referToOptions = ['ETAV'];

  return (
    <Box component="form" name="trip-request-referral-form" {...getFormProps(handleSubmit(onSubmit))} width="100%">
      <Box sx={{ py: 2 }}>
        <Stack spacing={2}>
          <TextField
            defaultValue={referToOptions[0]}
            select
            label="Refer to"
            {...register('referTo', { required: 'Please select a referral type' })}
            {...getFieldProps('referTo', errors)}
          >
            {referToOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Comments"
            multiline
            rows={7}
            {...register('referralComments')}
            {...getFieldProps('referralComments', errors)}
          />
          {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error on our end.</AlertTitle>
            {`Code: ${submissionError.code}: ${submissionError.message}`}
            <br />
            {submissionError.id && `ID: ${submissionError.id}`}
          </Alert>
          )}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 5 }}>
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Submit
          </LoadingButton>
          <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default TripRequestReferralForm;
